import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Users, Mail, Loader2, Clock } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const SEGMENT_LABELS = {
  all_users: { label: "All users (verified or not)", desc: "Every signup with an email that hasn't unsubscribed — includes Google-OAuth accounts + un-verified signups. Use for launch announcements only." },
  all_verified: { label: "All verified users", desc: "Users who verified their email + hasn't unsubscribed — safest deliverability" },
  zero_credits: { label: "Users with 0 credits", desc: "Verified users whose wallet is empty — perfect for top-up nudges" },
  inactive_30d: { label: "Inactive 30+ days", desc: "Users whose last activity (or signup) was over 30 days ago" },
};

/**
 * AdminBroadcasts — compose + send broadcast emails to a Mongo-filtered
 * segment via our own Resend account. Never sends without dry-run
 * confirmation. Rate-limited server-side to 1 broadcast/hour per admin.
 */
export default function AdminBroadcasts({ authHeader, onUnauthorized }) {
  const [segments, setSegments] = useState({});
  const [audienceSynced, setAudienceSynced] = useState(false);
  const [segment, setSegment] = useState("all_users");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [segs, l] = await Promise.all([
        axios.get(`${API}/admin/broadcasts/segments`, { headers: authHeader }),
        axios.get(`${API}/admin/broadcasts/log?limit=15`, { headers: authHeader }),
      ]);
      setSegments(segs.data.segments || {});
      setAudienceSynced(segs.data.audience_synced);
      setLog(l.data.broadcasts || []);
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized?.();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const send = async (dryRun) => {
    if (!subject.trim() || body.trim().length < 10) {
      toast.error("Subject + body (10+ chars) are required.");
      return;
    }
    const count = segments[segment] || 0;
    if (!dryRun && count === 0) {
      toast.error("Segment is empty — nothing to send.");
      return;
    }
    if (!dryRun && !window.confirm(`This will send to ${count} user${count === 1 ? "" : "s"}. This cannot be undone. Continue?`)) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/broadcasts/send`, {
        segment, subject: subject.trim(), body_html: body, dry_run: !!dryRun,
      }, { headers: authHeader });
      toast.success(dryRun ? `Dry-run sent to admin (id: ${res.data.id.slice(0, 8)})` : `Broadcast sent — ${res.data.delivered}/${res.data.recipients} delivered`);
      if (!dryRun) {
        setSubject("");
        setBody("");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  const syncAudience = async () => {
    try {
      const res = await axios.post(`${API}/admin/broadcasts/sync-audience`, {}, { headers: authHeader });
      toast.success(`Synced ${res.data.synced}/${res.data.total} users to Resend audience`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Audience sync failed");
    }
  };

  if (loading) return <div className="p-8 text-white/50 text-sm">Loading…</div>;

  return (
    <div className="p-6 md:p-8 space-y-8" data-testid="admin-broadcasts">
      {/* Segment overview */}
      <section>
        <h2 className="text-white text-xl font-light tracking-tight mb-1">Broadcast Composer</h2>
        <p className="text-white/45 text-[12px] leading-relaxed mb-4">
          Pick a segment, compose, dry-run to yourself, then send. Every email includes an auto-generated unsubscribe footer.
        </p>

        {/* RESEND_AUDIENCE_ID health check */}
        <div
          data-testid="resend-audience-health"
          className={`mb-5 px-4 py-3 rounded-lg border text-[12px] flex items-start gap-2 ${
            audienceSynced
              ? "border-emerald-400/25 bg-emerald-400/5 text-emerald-200/90"
              : "border-amber-400/30 bg-amber-400/5 text-amber-200/90"
          }`}
        >
          <span className="mt-0.5">{audienceSynced ? "✓" : "!"}</span>
          <div className="leading-relaxed">
            {audienceSynced ? (
              <>
                <span className="font-medium">Resend Audience: connected.</span>{" "}
                <span className="text-white/55">New signups are auto-added. You can broadcast safely.</span>
              </>
            ) : (
              <>
                <span className="font-medium">Resend Audience: not configured on this environment.</span>{" "}
                <span className="text-white/60">
                  Set <code className="px-1 rounded bg-black/40">RESEND_AUDIENCE_ID</code> in your{" "}
                  <strong>Deploy tab → Secrets</strong> (production) and in <code className="px-1 rounded bg-black/40">backend/.env</code> (preview),
                  then redeploy. Get the ID from{" "}
                  <a href="https://resend.com/audiences" target="_blank" rel="noreferrer" className="text-[#D4AF37] hover:underline">resend.com/audiences</a>.
                  Broadcasts still send without it — but new signups won&apos;t sync to your Resend list.
                </span>
              </>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {Object.entries(SEGMENT_LABELS).map(([key, meta]) => (
            <button
              key={key}
              type="button"
              data-testid={`broadcast-segment-${key}`}
              onClick={() => setSegment(key)}
              className={`text-left p-4 rounded-xl border transition ${
                segment === key
                  ? "border-[#D4AF37]/60 bg-[#D4AF37]/6"
                  : "border-white/10 bg-white/[0.02] hover:border-white/20"
              }`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className={`text-[13px] font-medium ${segment === key ? "text-[#D4AF37]" : "text-white/85"}`}>{meta.label}</span>
                <span className="text-white/50 text-xs">{segments[key] ?? 0}</span>
              </div>
              <div className="text-[11px] text-white/50 leading-relaxed">{meta.desc}</div>
            </button>
          ))}
        </div>

        {/* Composer */}
        <div className="space-y-3">
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject line — e.g. Your Facetra wallet is empty. Here's 50 free credits."
            maxLength={140}
            data-testid="broadcast-subject-input"
            className="w-full bg-[#0E0E0E] border border-white/10 rounded-lg px-4 py-3 text-white/90 text-sm placeholder:text-white/30 focus:border-[#D4AF37]/50 outline-none"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={"Body HTML — paste the coupon code from /coupons here.\n\n<p>Hey there,</p>\n<p>Use code <strong>LAUNCH15</strong> for 15% off your next credit pack.</p>\n<p><a href=\"https://facetra.studio/#pricing\">Top up now →</a></p>"}
            maxLength={20000}
            rows={10}
            data-testid="broadcast-body-input"
            className="w-full bg-[#0E0E0E] border border-white/10 rounded-lg px-4 py-3 text-white/90 text-sm placeholder:text-white/30 focus:border-[#D4AF37]/50 outline-none font-mono leading-relaxed"
          />
          <div className="flex items-center justify-between text-[11px] text-white/45">
            <span>Rate limit: 1 non-dry-run broadcast per hour. Header, unsubscribe footer + <code>List-Unsubscribe</code> auto-added.</span>
            <span>{body.length} / 20000</span>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <button
              type="button"
              onClick={() => send(true)}
              disabled={sending}
              data-testid="broadcast-dry-run-btn"
              className="px-4 py-2 rounded-lg border border-white/15 hover:border-white/30 text-white/75 text-[13px] transition disabled:opacity-50 inline-flex items-center gap-2"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
              Dry-run to admin
            </button>
            <button
              type="button"
              onClick={() => send(false)}
              disabled={sending || (segments[segment] || 0) === 0}
              data-testid="broadcast-send-btn"
              className="px-5 py-2 rounded-lg bg-[#D4AF37] hover:bg-[#E1BE45] text-black text-[13px] font-medium transition disabled:opacity-50 inline-flex items-center gap-2"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Send to {segments[segment] ?? 0} users
            </button>
          </div>
        </div>
      </section>

      {/* Audience sync */}
      <section className="pt-4 border-t border-white/8">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-white/85 text-[13px] font-medium tracking-tight flex items-center gap-2">
            <Users className="w-3.5 h-3.5 text-[#D4AF37]" /> Resend Audience
          </h3>
          <button
            type="button"
            onClick={syncAudience}
            data-testid="broadcast-sync-audience-btn"
            disabled={!audienceSynced}
            className="text-[12px] text-[#D4AF37] hover:underline disabled:text-white/30 disabled:no-underline"
          >
            {audienceSynced ? "Backfill all users to Resend →" : "RESEND_AUDIENCE_ID not configured"}
          </button>
        </div>
        <p className="text-[11px] text-white/45 leading-relaxed">
          New signups are auto-added to your Resend audience. Use "Backfill" once after setting <code>RESEND_AUDIENCE_ID</code> in <code>backend/.env</code>.
        </p>
      </section>

      {/* Broadcast log */}
      <section className="pt-4 border-t border-white/8">
        <h3 className="text-white/85 text-[13px] font-medium tracking-tight mb-3 flex items-center gap-2">
          <Clock className="w-3.5 h-3.5 text-[#D4AF37]" /> Recent broadcasts
        </h3>
        {log.length === 0 ? (
          <div className="text-white/45 text-xs">No broadcasts sent yet.</div>
        ) : (
          <div className="space-y-2" data-testid="broadcast-log">
            {log.map((b) => (
              <div key={b.id} className="flex items-center justify-between text-[12px] p-3 rounded-lg bg-white/[0.02] border border-white/8">
                <div>
                  <div className="text-white/85">{b.subject}</div>
                  <div className="text-white/45 text-[11px] mt-0.5">
                    {SEGMENT_LABELS[b.segment]?.label || b.segment}{" "}
                    · {new Date(b.sent_at).toLocaleString()}{" "}
                    {b.dry_run && <span className="ml-1 px-1.5 py-0.5 rounded bg-white/10 text-white/60 text-[10px] uppercase tracking-wide">dry-run</span>}
                  </div>
                </div>
                <div className="text-right text-white/70">
                  <div>{b.delivered}/{b.recipients_count}</div>
                  {b.failed > 0 && <div className="text-rose-300/70 text-[10px]">{b.failed} failed</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
